import { Link } from 'react-router-dom';
import {
  Sparkles,
  Sofa,
  Building2,
  Trees,
  Box,
  ImageIcon,
  LayoutTemplate,
  PencilLine,
  ScanLine,
  Settings2,
  BadgeDollarSign,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../seo/Seo';

const featureCards = [
  {
    Icon: Sparkles,
    title: 'AI Room Decoration',
    text: 'Create complete interior decoration concepts from a real room photo.',
  },
  {
    Icon: Sofa,
    title: 'AI Room Furniture Replacement',
    text: 'Replace furniture while keeping the room structure recognizable.',
  },
  {
    Icon: Building2,
    title: 'Exterior Renovation',
    text: 'Transform building facades with new exterior styles, materials, and colors.',
  },
  {
    Icon: Trees,
    title: 'AI Garden Design',
    text: 'Generate landscaping, patio, backyard, and outdoor living ideas from photos.',
  },
  {
    Icon: Box,
    title: '2D Floor Plan to 3D View',
    text: 'Convert architectural floor plans into 3D-style home and room visualizations.',
  },
  {
    Icon: ImageIcon,
    title: 'Floor Plan to Room Image',
    text: 'Select a room from a floor plan and generate a realistic interior preview.',
  },
  {
    Icon: LayoutTemplate,
    title: 'AI Floor Plan Creator',
    text: 'Describe home details and let AI create a 2D architectural floor plan.',
  },
  {
    Icon: PencilLine,
    title: 'Draw Your Floor Plan',
    text: 'Sketch and edit a custom 2D floor plan manually when you need full control.',
  },
];

const valueCards = [
  {
    Icon: ScanLine,
    title: 'Photo and plan based workflows',
    text: 'Start from room photos, exterior images, gardens, or 2D floor plans, then generate visuals made for that exact space.',
  },
  {
    Icon: Settings2,
    title: 'Creative control across every tool',
    text: 'Choose styles, colors, materials, change levels, room types, render settings, and written instructions before generating.',
  },
  {
    Icon: BadgeDollarSign,
    title: 'Affordable design exploration',
    text: 'Compare architecture, interior, exterior, and landscape directions before spending on furniture, renovation, or build work.',
  },
];

function FeaturesPage() {
  const newDesignTo = '/ai-tools/ai-room-decoration';

  return (
    <div className="page landing-bg features-page">
      <Seo
        title="AI Home Design Features | MyRoomsAI"
        description="Explore MyRoomsAI features for architecture, interior design, exterior renovation, garden design, and floor plan visualization in one AI design workspace."
        canonicalPath="/features/"
      />
      <Navbar />

      {/* Hero */}
      <section className="container section fp-hero">
        <span className="credits-badge credits-badge--lilac">All-in-one AI design workspace</span>
        <h1 className="fp-hero-heading">
          AI tools for{' '}
          <span className="new-design-page-heading-accent">
            architecture, interiors, and exteriors
          </span>
        </h1>
        <p className="credits-subtitle">
          Plan and visualize rooms, facades, gardens, and floor plans from one place with
          flexible AI tools for real design decisions.
        </p>
      </section>

      {/* 8-card feature grid */}
      <section className="container section fp-grid-section">
        <div className="features-grid">
          {featureCards.map(({ Icon, title, text }) => (
            <article key={title} className="feature-card fp-feature-card">
              <span className="feature-card-icon-badge">
                <Icon size={20} strokeWidth={1.8} className="fp-lucide-icon" aria-hidden="true" />
              </span>
              <div className="feature-card-body">
                <h3 className="feature-card-title">{title}</h3>
                <p className="feature-card-desc">{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Designed for Complete Spaces */}
      <section className="container section">
        <h2 className="fp-section-heading">Designed for Complete Spaces</h2>
        <div className="fp-value-grid">
          {valueCards.map(({ Icon, title, text }) => (
            <article key={title} className="fp-value-card">
              <span className="fp-value-icon-badge">
                <Icon size={22} strokeWidth={1.8} className="fp-lucide-icon" aria-hidden="true" />
              </span>
              <div>
                <h3 className="fp-value-title">{title}</h3>
                <p className="fp-value-desc">{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container section fp-cta-section">
        <div className="fp-cta-card fp-cta-card--trust">
          <div className="fp-cta-inner">
            <div className="fp-cta-copy">
              <h2 className="fp-cta-heading">Ready to design your next space?</h2>
              <p className="fp-cta-text">
                Choose an AI tool for interiors, exteriors, gardens, or floor plans and generate a clear visual direction in minutes.
              </p>
            </div>
            <div className="fp-cta-actions-wrap">
              <div className="fp-cta-actions">
                <Link to={newDesignTo} className="btn btn-primary">
                  Explore AI Tools
                </Link>
                <Link to="/real-transformations" className="btn btn-ghost">
                  View Real Transformations
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FeaturesPage;
